import React, { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import useCachedQuery, { invalidateQueries } from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useUrlState from '../../hooks/useUrlState';
import { searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

const BekleyenOnaylarPage = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  // Arama URL'e bağlı: onay detayına gidip geri dönünce / yenileyince korunur.
  const [search, setSearch] = useUrlState('arama', '', { debounceMs: 300 });
  const debouncedSearch = useDebouncedValue(search, 300);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const {
    data: approvals = [],
    isLoading,
    isValidating,
    mutate,
    revalidate
  } = useCachedQuery(
    ['onay', userCacheKey, 'pending-approvals'],
    async () => {
      const res = await fetch(`${API_URL}/api/approvals/pending`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 15000 }
  );

  const filteredApprovals = useMemo(() => {
    const q = (debouncedSearch || '').trim().toLowerCase();
    if (!q) return approvals;
    return approvals.filter(app =>
      searchMatchPre(app.siparis_no, q) ||
      searchMatchPre(app.tedarikci_ad, q)
    );
  }, [approvals, debouncedSearch]);

  const handleDecision = async (approvalId, decision, reason = null) => {
    const previousData = mutate(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.filter(item => item.approval_id !== approvalId);
    }, { revalidate: false });

    setMessage('İşlem başarılı!');
    setTimeout(() => setMessage(''), 3000);

    try {
      const res = await fetch(`${API_URL}/api/approvals/${approvalId}/decide`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ decision, reason, user_id: user?.id })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      invalidateQueries(['siparis', 'dashboard', 'talep']);
      await revalidate({ force: true });
    } catch (err) {
      mutate(previousData, { revalidate: false });
      setMessage('İşlem tamamlanamadı.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div>
      <Header title="Bekleyen Onaylar" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {message && (
          <div style={{ background: '#d4edda', padding: '12px 20px', marginBottom: '20px', borderRadius: radius.sm, color: '#155724', borderLeft: `4px solid ${styles.success}` }}>
            {message}
          </div>
        )}

        <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden', boxShadow: shadows.sm }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 15px', borderBottom: `1px solid ${colors.border}`, gap: '10px', flexWrap: 'wrap' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sipariş no / tedarikçi ara"
              style={{ width: '280px', maxWidth: '100%', border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '8px 10px', fontSize: '13px' }}
            />
            {isValidating && !isLoading && (
              <span style={{ fontSize: '12px', color: colors.textSecondary, background: '#f1f3f5', borderRadius: radius.lg, padding: '4px 10px' }}>
                Güncelleniyor...
              </span>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: styles.secondary, color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left' }}>Sipariş No</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Tedarikçi</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Tutar</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Tarih</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Onay Rolu</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, row) => (
                  <tr key={`skeleton-${row}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {Array.from({ length: 6 }).map((__, col) => (
                      <td key={`skeleton-${row}-${col}`} style={{ padding: '15px' }}>
                        <div style={{ height: '12px', borderRadius: radius.sm, background: '#e9ecef', width: col === 1 ? '85%' : '65%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredApprovals.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                    Onay bekleyen siparis bulunmuyor.
                  </td>
                </tr>
              ) : filteredApprovals.map(app => (
                <tr key={app.approval_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '15px', fontWeight: 'bold' }}>{app.siparis_no}</td>
                  <td style={{ padding: '15px' }}>{app.tedarikci_ad || '-'}</td>
                  <td style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold' }}>
                    {app.genel_toplam ? `₺${Number(app.genel_toplam).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}
                  </td>
                  <td style={{ padding: '15px' }}>{app.siparis_tarihi ? new Date(app.siparis_tarihi).toLocaleDateString('tr-TR') : '-'}</td>
                  <td style={{ padding: '15px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: radius.lg,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: '#e8d4f0',
                      color: '#6f42c1'
                    }}>
                      {app.required_role === 'GENEL_MUDUR' ? 'Genel Mudur' : app.required_role || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleDecision(app.approval_id, 'APPROVED')}
                      style={{ marginRight: '8px', padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
                    >
                      Onayla
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Red nedeni:');
                        if (reason) handleDecision(app.approval_id, 'REJECTED', reason);
                      }}
                      style={{ padding: '8px 16px', background: styles.danger, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
                    >
                      Reddet
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BekleyenOnaylarPage;
